import { useState } from "react";
import { useFocusVisible } from "react-aria";

type PlayPauseAction = "play" | "pause";

/**
 * The action a play/pause button should show and perform. Hovering or
 * keyboard-focusing the button captures the action at that moment, so it
 * doesn't flip under the pointer when playback proceeds to the next fragment.
 */
export function useCapturedPlayPauseAction(isPlaying: boolean) {
  const { isFocusVisible } = useFocusVisible();
  const [focusAction, setFocusAction] = useState<PlayPauseAction>();
  const [hoverAction, setHoverAction] = useState<PlayPauseAction>();
  const focusVisibleAction = isFocusVisible ? focusAction : undefined;

  const liveAction: PlayPauseAction = isPlaying ? "pause" : "play";

  return {
    action: hoverAction ?? focusVisibleAction ?? liveAction,
    /** Whether the button is currently hovered or keyboard-focused. */
    isInteracting:
      hoverAction !== undefined || focusVisibleAction !== undefined,
    captureProps: {
      onFocus: () => setFocusAction(liveAction),
      onBlur: () => setFocusAction(undefined),
      onHoverStart: () => setHoverAction(liveAction),
      onHoverEnd: () => setHoverAction(undefined),
    },
    /** Call after performing an action, so a captured action flips to its reciprocal. */
    onActionPerformed: (performed: PlayPauseAction) => {
      const flip = (captured: PlayPauseAction | undefined) =>
        captured === performed
          ? performed === "play"
            ? "pause"
            : "play"
          : captured;
      setFocusAction(flip);
      setHoverAction(flip);
    },
  };
}
